import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, User, Loader2, Eye, Download, X, Trash2, FileCheck, Plus, Briefcase } from 'lucide-react';

export default function AdminApplications() {
  const [applications, setApplications] = useState([]);
  const [testSessions, setTestSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  
  // Guthaben State für das Modal
  const [editBalance, setEditBalance] = useState('');

  // Zuweisung State
  const [selectedSessionToAssign, setSelectedSessionToAssign] = useState('');

  // Vertrags-Auswahl Modal
  const [contractModalApplicant, setContractModalApplicant] = useState(null);
  const [selectedContractType, setSelectedContractType] = useState('Minijob (603€-Basis)');

  // Neuen Tester erstellen State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTesterData, setNewTesterData] = useState({
    first_name: '', last_name: '', email: '', password: '', contract_type: 'Minijob (603€-Basis)'
  });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => { 
    fetchApplications(); 
    fetchTestSessions();
  }, []);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/applications', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setApplications(await response.json());
    } catch (err) { console.error(err); } finally { setIsLoading(false); }
  };

  const fetchTestSessions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/test-sessions', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setTestSessions(await response.json());
    } catch (err) { console.error(err); }
  };

  const handleStatusChange = async (testerId, newStatus, contractType = null) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/applications/${testerId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus, contract_type: contractType })
      });
      if (response.ok) {
        setApplications(apps => apps.map(app => app.id === testerId ? { ...app, status: newStatus, contract_type: contractType || app.contract_type } : app));
        setSelectedApplicant(null);
        setContractModalApplicant(null);
      }
    } catch (err) { console.error(err); }
  };

  const handleUpdateBalance = async (testerId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/employees/${testerId}/balance`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ balance: parseFloat(editBalance) || 0 })
      });
      
      if (response.ok) {
        alert("Guthaben erfolgreich aktualisiert!");
        setApplications(apps => apps.map(app => app.id === testerId ? { ...app, balance: parseFloat(editBalance) || 0 } : app));
        setSelectedApplicant(prev => ({ ...prev, balance: parseFloat(editBalance) || 0 }));
      } else {
        alert("Fehler beim Aktualisieren des Guthabens.");
      }
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (testerId) => {
    if (!window.confirm("Achtung: Möchten Sie diesen Account wirklich unwiderruflich löschen?")) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/applications/${testerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setApplications(apps => apps.filter(app => app.id !== testerId));
        setSelectedApplicant(null);
      }
    } catch (err) { console.error(err); }
  };

  const handleCreateTester = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newTesterData)
      });
      
      if (response.ok) {
        setShowCreateModal(false);
        setNewTesterData({ first_name: '', last_name: '', email: '', password: '', contract_type: 'Minijob (603€-Basis)' });
        fetchApplications();
        alert("Tester-Account erfolgreich erstellt und freigeschaltet!");
      } else {
        const errorData = await response.json();
        alert(`Fehler: ${errorData.detail}`);
      }
    } catch (err) { console.error(err); alert("Netzwerkfehler."); } finally { setIsCreating(false); }
  };

  const handleAssignJob = async () => {
    if (!selectedSessionToAssign || !selectedApplicant) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/employees/${selectedApplicant.id}/assign-job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ session_id: selectedSessionToAssign })
      });
      if (response.ok) {
        alert("Auftrag wurde dem Tester erfolgreich zugewiesen!");
        setSelectedSessionToAssign('');
      } else {
        alert("Fehler bei der Zuweisung.");
      }
    } catch (err) { console.error(err); }
  };

  const handleSelectAll = (e) => { e.target.checked ? setSelectedIds(applications.map(app => app.id)) : setSelectedIds([]); };
  const handleSelectOne = (e, id) => { e.target.checked ? setSelectedIds([...selectedIds, id]) : setSelectedIds(selectedIds.filter(selectedId => selectedId !== id)); };

  const exportToCSV = () => {
    const selectedApps = applications.filter(app => selectedIds.includes(app.id));
    if (selectedApps.length === 0) return;
    const headers = ["ID", "Name", "E-Mail", "Telefon", "Straße", "PLZ", "Stadt", "Geburtsdatum", "Nationalität", "Position", "IBAN", "Steuer-ID", "SV-Nummer", "Status", "Guthaben", "Datum"];
    const csvRows = [headers.join(",")];
    selectedApps.forEach(app => {
      const row = [app.id, app.name, app.email, app.phone, app.street, app.zip_code, app.city, app.birth_date, app.nationality, app.role, app.iban, app.tax_id, app.social_security_num, app.status, app.balance, app.date];
      csvRows.push(row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `bewerber_export.csv`);
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const handleDownloadCV = (applicant) => {
    const cvContent = `LEBENSLAUF / BEWERBERAKTE\n\nName: ${applicant.name}\nE-Mail: ${applicant.email}\nTelefon: ${applicant.phone}\n\nAdresse:\n${applicant.street}\n${applicant.zip_code} ${applicant.city}\n\nPersönliches:\nGeburtsdatum: ${applicant.birth_date}\nNationalität: ${applicant.nationality}\n\nAbrechnungsdaten:\nIBAN: ${applicant.iban}\nSteuer-ID: ${applicant.tax_id}\nSV-Nummer: ${applicant.social_security_num}\n\nBewerbung für:\nPosition: ${applicant.role}\nAnstellungsart: ${applicant.employment_type}\nDatum der Bewerbung: ${applicant.date}`;
    const blob = new Blob([cvContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `Lebenslauf_${applicant.name.replace(/\s+/g, '_')}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div><h1 className="text-2xl font-bold text-primary">Bewerber & Tester-Verwaltung</h1></div>
        <div className="flex items-center gap-3 w-full md:w-auto flex-wrap">
          {selectedIds.length > 0 && <button onClick={exportToCSV} className="bg-white border border-border text-secondary px-4 py-2.5 rounded-md font-medium hover:bg-slate-50 flex items-center gap-2 shadow-sm"><Download className="w-4 h-4" /> CSV Export</button>}
          
          <button onClick={() => setShowCreateModal(true)} className="bg-accent text-white px-4 py-2.5 rounded-md font-medium hover:bg-accent/90 flex items-center gap-2 shadow-sm">
            <Plus className="w-4 h-4" /> Neuen Tester anlegen
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto min-h-[300px]">
          {isLoading ? <div className="flex justify-center h-64 items-center"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div> : (
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead>
                <tr className="bg-slate-50 border-b border-border text-sm text-secondary">
                  <th className="p-4 w-12"><input type="checkbox" onChange={handleSelectAll} checked={selectedIds.length === applications.length && applications.length > 0} className="rounded" /></th>
                  <th className="p-4 font-semibold">Tester / Bewerber</th>
                  <th className="p-4 font-semibold">Position</th>
                  <th className="p-4 font-semibold">Guthaben</th>
                  <th className="p-4 font-semibold">Status</th>
                  <th className="p-4 font-semibold text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => {
                  const isPending = app.status === 'pending_interview' || app.status === 'pending';
                  
                  return (
                  <tr key={app.id} className="border-b border-border hover:bg-slate-50/50">
                    <td className="p-4"><input type="checkbox" checked={selectedIds.includes(app.id)} onChange={(e) => handleSelectOne(e, app.id)} className="rounded" /></td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center"><User className="w-5 h-5 text-secondary" /></div>
                        <div><p className="font-medium text-primary">{app.name}</p><p className="text-xs text-muted-foreground">{app.id}</p></div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-secondary">{app.role}</td>
                    <td className="p-4 text-sm font-bold text-green-600">{(app.balance || 0).toFixed(2).replace('.', ',')} €</td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${isPending ? 'bg-yellow-100 text-yellow-800' : app.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {isPending ? 'Ausstehend' : app.status === 'active' ? 'Aktiv (Tester)' : 'Abgelehnt'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => { setSelectedApplicant(app); setEditBalance(app.balance || 0); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" title="Details ansehen"><Eye className="w-5 h-5" /></button>
                        <button onClick={() => handleDelete(app.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-md" title="Bewerber löschen"><Trash2 className="w-5 h-5" /></button>
                      </div>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* DETAIL MODAL BEWERBER/TESTER */}
      {selectedApplicant && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col animate-in zoom-in-95">
            <div className="flex justify-between items-center p-6 border-b border-border bg-white rounded-t-xl">
              <div>
                <h2 className="text-xl font-bold text-primary">Tester Akte</h2>
                <p className="text-sm text-muted-foreground font-mono mt-1">ID: {selectedApplicant.id}</p>
              </div>
              <button onClick={() => setSelectedApplicant(null)} className="text-slate-400 hover:text-red-500 p-2 bg-slate-50 rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6 overflow-y-auto">
              {/* --- Auftrag zuweisen (nur für aktive Tester) --- */}
              {selectedApplicant.status === 'active' && (
                <div className="bg-sky-50 border border-sky-200 p-5 rounded-xl shadow-sm">
                  <h3 className="font-bold text-primary mb-2 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-accent" /> Test-Auftrag manuell zuweisen
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">Wähle einen aktiven Auftrag aus. Dieser wird sofort im Portal des Testers unter "In Bearbeitung" angezeigt.</p>
                  
                  <div className="flex gap-3">
                    <select 
                      className="flex-1 p-2.5 border border-slate-300 rounded-md outline-none focus:border-accent bg-white text-sm"
                      value={selectedSessionToAssign}
                      onChange={e => setSelectedSessionToAssign(e.target.value)}
                    >
                      <option value="">-- Wähle einen Auftrag aus --</option>
                      {testSessions.map(ts => (
                        <option key={ts.id} value={ts.id}>{ts.title} ({ts.id})</option>
                      ))}
                    </select>
                    <button 
                      onClick={handleAssignJob}
                      disabled={!selectedSessionToAssign}
                      className="bg-accent text-white px-5 py-2.5 rounded-md font-medium hover:bg-accent/90 disabled:opacity-50 transition-colors text-sm"
                    >
                      Zuweisen
                    </button>
                  </div>
                </div>
              )}

              {/* Guthabenverwaltung (nur für aktive Tester) */}
              {selectedApplicant.status === 'active' && (
                <div className="bg-green-50 p-5 rounded-lg border border-green-200 shadow-sm">
                  <h3 className="font-semibold text-green-800 mb-3 border-b border-green-200 pb-2">Guthaben & Auszahlungen</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <span className="text-green-700 block mb-1 text-sm font-medium">Kontostand (€)</span>
                      <input 
                        type="number" 
                        step="0.01"
                        value={editBalance} 
                        onChange={(e) => setEditBalance(e.target.value)} 
                        className="w-full p-2.5 border border-green-300 rounded-md outline-none focus:border-green-500 bg-white text-slate-800 font-bold"
                      />
                    </div>
                    <button 
                      onClick={() => handleUpdateBalance(selectedApplicant.id)}
                      className="mt-6 bg-green-600 text-white px-5 py-2.5 rounded-md font-medium hover:bg-green-700 transition-colors text-sm shadow-sm"
                    >
                      Speichern
                    </button>
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    Nach einer Auszahlung kannst du den Betrag hier manuell anpassen oder auf 0.00 zurücksetzen.
                  </p>
                </div>
              )}

              <div>
                <h3 className="font-semibold text-primary mb-3 border-b border-slate-100 pb-2">Stammdaten</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground block mb-1">Voller Name</span> <span className="font-medium text-primary">{selectedApplicant.name}</span></div>
                  <div><span className="text-muted-foreground block mb-1">Geburtsdatum</span> <span className="text-primary">{selectedApplicant.birth_date}</span></div>
                  <div><span className="text-muted-foreground block mb-1">E-Mail</span> <span className="text-accent">{selectedApplicant.email}</span></div>
                  <div><span className="text-muted-foreground block mb-1">Telefon</span> <span className="text-primary">{selectedApplicant.phone}</span></div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground block mb-1">Wohnadresse</span>
                    <span className="text-primary">{selectedApplicant.street}<br/>{selectedApplicant.zip_code} {selectedApplicant.city}</span>
                  </div>
                  <div className="col-span-2"><span className="text-muted-foreground block mb-1">Nationalität</span> <span className="text-primary">{selectedApplicant.nationality}</span></div>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h3 className="font-semibold text-primary mb-3 border-b border-slate-200 pb-2">Abrechnungs- & Personaldaten</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><span className="text-muted-foreground block mb-1">IBAN</span> <span className="font-mono text-xs font-bold">{selectedApplicant.iban}</span></div>
                  <div><span className="text-muted-foreground block mb-1">Steuer-ID</span> <span className="font-mono text-xs font-bold">{selectedApplicant.tax_id}</span></div>
                  <div><span className="text-muted-foreground block mb-1">SV-Nummer</span> <span className="font-mono text-xs font-bold">{selectedApplicant.social_security_num}</span></div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-primary mb-3 border-b border-slate-100 pb-2">Dokumente</h3>
                <div className="flex justify-between items-center bg-slate-50 p-4 rounded-lg border border-border">
                  <div>
                    <span className="text-muted-foreground block mb-1">Angehängter Lebenslauf</span> 
                    <span className="font-medium text-primary">{selectedApplicant.cv_filename}</span>
                  </div>
                  <button onClick={() => handleDownloadCV(selectedApplicant)} className="text-accent hover:underline text-sm font-medium flex items-center gap-1">
                    <Download className="w-4 h-4"/> Akte Herunterladen
                  </button>
                </div>
              </div>
            </div>
            
            {/* Action Buttons für ausstehende Bewerber */}
            {(selectedApplicant.status === 'pending_interview' || selectedApplicant.status === 'pending') && (
              <div className="p-6 border-t border-border bg-slate-50 flex justify-end gap-3 rounded-b-xl shrink-0">
                 <button onClick={() => handleStatusChange(selectedApplicant.id, 'rejected')} className="px-6 py-2.5 text-red-600 bg-white border border-red-200 hover:bg-red-50 rounded-md font-medium">Ablehnen</button>
                 <button onClick={() => { setContractModalApplicant(selectedApplicant); setSelectedApplicant(null); }} className="px-6 py-2.5 text-white bg-green-600 hover:bg-green-700 rounded-md font-medium flex items-center gap-2">
                   <CheckCircle className="w-5 h-5" /> Bewerber Annehmen & Vertrag ausstellen
                 </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL ZUR AUSWAHL DES VERTRAGSTYPS BEI ANNAHME */}
      {contractModalApplicant && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in zoom-in-95">
            <h2 className="text-xl font-bold text-primary mb-2 flex items-center gap-2">
              <FileCheck className="w-6 h-6 text-green-600" /> Arbeitsvertrag ausstellen
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Wählen Sie den Vertragstyp für <strong>{contractModalApplicant.name}</strong>. Der Vertrag wird automatisch generiert und im Tester-Portal bereitgestellt.
            </p>

            <div className="space-y-3 mb-6">
              <label className="flex items-center p-3 border border-border rounded-lg cursor-pointer hover:bg-slate-50">
                <input type="radio" name="contract_type" value="Minijob (603€-Basis)" checked={selectedContractType === 'Minijob (603€-Basis)'} onChange={e => setSelectedContractType(e.target.value)} className="mr-3 text-accent" />
                <div>
                  <span className="font-bold text-primary text-sm block">Minijob-Vertrag</span>
                  <span className="text-xs text-muted-foreground">Geringfügige Beschäftigung auf 603€-Basis</span>
                </div>
              </label>

              <label className="flex items-center p-3 border border-border rounded-lg cursor-pointer hover:bg-slate-50">
                <input type="radio" name="contract_type" value="Teilzeitvertrag" checked={selectedContractType === 'Teilzeitvertrag'} onChange={e => setSelectedContractType(e.target.value)} className="mr-3 text-accent" />
                <div>
                  <span className="font-bold text-primary text-sm block">Teilzeit-Arbeitsvertrag</span>
                  <span className="text-xs text-muted-foreground">Reguläre Teilzeitbeschäftigung</span>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setContractModalApplicant(null)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-md font-medium text-sm">Abbrechen</button>
              <button onClick={() => handleStatusChange(contractModalApplicant.id, 'active', selectedContractType)} className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white font-medium text-sm rounded-md shadow-sm">
                Vertrag Generieren & Freischalten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL UM EINEN TESTER MANUELL ANZULEGEN */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full flex flex-col animate-in zoom-in-95">
            <div className="flex justify-between items-center p-5 border-b border-border">
              <h2 className="font-bold text-primary text-lg">Neuen Tester direkt anlegen</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-red-500"><X className="w-5 h-5" /></button>
            </div>
            
            <form onSubmit={handleCreateTester} className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Erstellen Sie einen Account für Tester, die über externe Portale kommen. Der Account ist <strong>sofort freigeschaltet (Aktiv)</strong> und der Vertrag wird bereitgestellt.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Vorname *</label>
                  <input required type="text" className="w-full p-2.5 border border-border rounded-md outline-none focus:border-accent" value={newTesterData.first_name} onChange={e => setNewTesterData({...newTesterData, first_name: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nachname *</label>
                  <input required type="text" className="w-full p-2.5 border border-border rounded-md outline-none focus:border-accent" value={newTesterData.last_name} onChange={e => setNewTesterData({...newTesterData, last_name: e.target.value})} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">E-Mail (Login-Name) *</label>
                <input required type="email" className="w-full p-2.5 border border-border rounded-md outline-none focus:border-accent" value={newTesterData.email} onChange={e => setNewTesterData({...newTesterData, email: e.target.value})} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Start-Passwort *</label>
                <input required type="text" placeholder="z.B. Start1234!" className="w-full p-2.5 border border-border rounded-md outline-none focus:border-accent" value={newTesterData.password} onChange={e => setNewTesterData({...newTesterData, password: e.target.value})} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Vertragstyp *</label>
                <select 
                  className="w-full p-2.5 border border-border rounded-md outline-none focus:border-accent bg-white"
                  value={newTesterData.contract_type} 
                  onChange={e => setNewTesterData({...newTesterData, contract_type: e.target.value})}
                >
                  <option value="Minijob (603€-Basis)">Minijob-Vertrag</option>
                  <option value="Teilzeitvertrag">Teilzeit-Vertrag</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-md font-medium text-sm">Abbrechen</button>
                <button type="submit" disabled={isCreating} className="px-5 py-2 bg-primary hover:bg-secondary text-white font-medium text-sm rounded-md shadow-sm disabled:opacity-50">
                  {isCreating ? 'Wird erstellt...' : 'Account erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}